// import { v4 as uuidv4 } from 'uuid';

import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';
import store from 'app-components/utils/redux-store';

import deterministicGuid from './deterministicGuid';
import { mergeMintImportSharedState } from './mintImportSharedState';

// temporary hack
const investmentTransactionTypeMap = {
  Buy: 'BUY',
  Sell: 'SELL',
  'Dividend & Cap Gains': 'STOCK_DIVIDEND',
};
const isMintInvestmentTransaction = (mintTransaction) =>
  mintTransaction.type === 'InvestmentTransaction' &&
  mintTransaction.category?.categoryType === 'NO_CATEGORY' &&
  (investmentTransactionTypeMap[mintTransaction.category?.name] ||
    mintTransaction.commission ||
    mintTransaction.price ||
    mintTransaction.quantity ||
    mintTransaction.symbol);

const getInvestmentTxnType = (mintTransaction) => {
  let investmentTxnType = investmentTransactionTypeMap[mintTransaction.category?.name];
  if (!investmentTxnType) {
    investmentTxnType = mintTransaction.amount > 0 ? 'SELL' : 'BUY';
  }
  return investmentTxnType;
};

const importTransactions = async (fileJS, simplifiAccountsByMintId, simplifiCOAsByMintId, simplifiTagsByMintId) => {
  let results = false;
  const key = 'TRANSACTIONS';
  try {
    const mintTransactionsById = fileJS.transactions.reduce((result, mintTransaction) => {
      if (mintTransaction.parentId) {
        const splits = result.get(mintTransaction.parentId)?.splits || [];
        splits.push({
          ...mintTransaction,
          // clientId: uuidv4().toUpperCase(),
          clientId: deterministicGuid(mintTransaction.id),
        });
        const amount = splits.reduce((sum, split) => (sum += split.amount), 0); // eslint-disable-line no-param-reassign, no-return-assign
        const transaction = {
          ...mintTransaction,
          id: mintTransaction.parentId,
          // clientId: uuidv4().toUpperCase(),
          clientId: deterministicGuid(mintTransaction.id),
          parentId: undefined,
          splits,
          amount,
          description: mintTransaction.fiData?.inferredDescription,
          category: undefined,
          tagData: undefined,
          notes: undefined,
        };
        result.set(transaction.id, transaction);
      } else {
        const transaction = {
          ...mintTransaction,
          // clientId: uuidv4().toUpperCase(),
          clientId: deterministicGuid(mintTransaction.id),
        };
        result.set(transaction.id, transaction);
      }
      return result;
    }, new Map());

    mergeMintImportSharedState({ key, progress: 10 });

    const mintTransactions = [...mintTransactionsById.values()];
    const chunkSize = 1000;
    let chunk = mintTransactions.splice(0, chunkSize);
    let index = 0;
    while (chunk.length) {
      // eslint-disable-next-line no-await-in-loop,@typescript-eslint/no-loop-func
      await new Promise((resolve, reject) => {
        store.dispatch(
          transactionsActions.batchTransactions(
            chunk.map((mintTransaction) => {
              const accountId = simplifiAccountsByMintId.get(mintTransaction.accountId)?.id;
              assert(accountId, 'account not found for mintAccountId', { mintTransaction });
              const tags = mintTransaction.tagData?.tags?.map((mintTag) => {
                const id = simplifiTagsByMintId.get(mintTag.id)?.id;
                assert(id, 'tag not found for mintTagId', { mintTag });
                return { id };
              });
              if (isMintInvestmentTransaction(mintTransaction)) {
                const investmentTxnType = getInvestmentTxnType(mintTransaction); // e.g. BUY
                return transactionsTypes.mkInvestmentTransaction({
                  clientId: mintTransaction.clientId,
                  postedOn: mintTransaction.date,
                  accountId,
                  amount: mintTransaction.amount,
                  tags,
                  payee: mintTransaction.description,
                  memo: mintTransaction.notes,
                  source: 'CLIENT_AGGREGATED', // TODO: MINT
                  state: 'CLEARED',
                  investmentTxnType,

                  symbol: mintTransaction.symbol,
                  // localSecurityId do we need to handle symbols manually?
                  securityIdType: 'QCSSYMBOL',
                  securityId: mintTransaction.symbol,
                  units: mintTransaction.quantity,
                  commission: mintTransaction.commission,

                  ...(mintTransaction.fiData && {
                    cpData: {
                      id: mintTransaction.id,
                      postedOn: mintTransaction.fiData.date,
                      payee: mintTransaction.fiData.description,
                      amount: mintTransaction.fiData.amount,
                      inferredPayee: mintTransaction.fiData.inferredDescription,
                    },
                  }),
                });
              }

              const coa = mintTransaction.splits
                ? undefined
                : assert(simplifiCOAsByMintId.get(mintTransaction.category?.id), 'can not find coa', {
                    mintTransaction,
                  });
              const splitItems = mintTransaction.splits?.map((mintSplit) => {
                const splitCOA = assert(simplifiCOAsByMintId.get(mintSplit.category.id), 'can not find split coa', {
                  mintSplit,
                });
                const splitTags = mintSplit.tagData?.tags?.map((mintTag) => {
                  const id = simplifiTagsByMintId.get(mintTag.id)?.id;
                  assert(id, 'tag not found for mintTagId', { mintTag });
                  return { id };
                });
                return {
                  clientId: mintSplit.clientId,
                  amount: mintSplit.amount,
                  coa: splitCOA,
                  memo: mintSplit.notes,
                  tags: splitTags,
                };
              });
              return transactionsTypes.mkCashFlowTransaction({
                clientId: mintTransaction.clientId,
                postedOn: mintTransaction.date,
                accountId,
                amount: mintTransaction.amount,
                tags,
                payee: mintTransaction.description,
                memo: mintTransaction.notes,
                source: 'CLIENT_AGGREGATED', // TODO: MINT
                state: 'CLEARED',
                coa,
                type: 'CASH_FLOW',
                ...(splitItems && { split: { items: splitItems } }),
                ...(mintTransaction.fiData && {
                  cpData: {
                    id: mintTransaction.id,
                    postedOn: mintTransaction.fiData.date,
                    payee: mintTransaction.fiData.description,
                    amount: mintTransaction.fiData.amount,
                    inferredPayee: mintTransaction.fiData.inferredDescription,
                  },
                }),
              });
            }),
            { resolve, reject },
          ),
        );
      });

      index += chunkSize;
      mergeMintImportSharedState({ key, progress: 10 + Math.trunc((100 * 0.8 * index) / mintTransactionsById.size) }); // 10-90%

      chunk = mintTransactions.splice(0, chunkSize);
    }

    results = true;

    mergeMintImportSharedState({
      key,
      progress: 100,
      [key]: {
        importedCount: mintTransactionsById.size,
        earliestDate: [...mintTransactionsById.values()].reduce(
          (date, mintTransaction) => (date < mintTransaction.date ? date : mintTransaction.date),
          null,
        ),
      },
    });
  } catch (error) {
    assert(false, error);
    mergeMintImportSharedState({ error: key, [key]: { error } });
  }

  return results;
};
export default importTransactions;
